import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';

import { Industries } from '@utils/industries';
import {
  createCompanyManagerFailureAction,
  createCompanyManagerSuccessAction,
  UsersActions,
  createCompanyManagerLoadingSelector,
} from '@modules/company-manager/users/application';
import {
  CompanyActions,
  validateCompanyFailureAction,
  validateCompanySuccessAction,
  validateCompanyLoadingSelector,
} from '@modules/super-admin/company/application';
import { APP_ROUTES } from '@typings/app-routes.constant';
import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';
import {
  AuthenticationDataModel,
  AuthenticationNgRxActions,
} from '@geekbears/gb-ngrx-auth-basic';
import { SessionService } from '../../../../../core/application/session.service';
import { AnalyticsService } from '@core/application';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.page.html',
  styleUrls: ['./sign-up.page.scss'],
})
export class SignUpPage implements OnInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  form!: FormGroup;
  industries = Industries;
  companyExists: string = '';
  createManagerLoading$: Observable<boolean | undefined>;
  validateCompanyName$: Observable<boolean | undefined>;
  isValid: boolean = false;
  url: any;
  isLoading: boolean = false;

  constructor(
    private store$: Store,
    private actions$: Actions,
    private fb: FormBuilder,
    private notificationService: NotificationsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private analytics: AnalyticsService
  ) {
    this.validateCompanyName$ = this.store$.select(
      validateCompanyLoadingSelector
    );
    this.createManagerLoading$ = this.store$
      .select(createCompanyManagerLoadingSelector)
      .pipe(
        tap((status) => {
          if (status) this.isLoading = true;
        })
      );
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      jobTitle: [''],
      department: [''],
      companyName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ],
      ],
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
    });
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  ngOnInit(): void {
    this.sessionService.clearSession();
    this.getParams();
    this.onFailureActions();
    this.onValidateCompanyNameSuccess();
    this.actions$
      .pipe(
        ofType(createCompanyManagerSuccessAction),
        filter((action) => {
          return !isNil(action);
        }),
        map((action) => {
          if (action) {
            // Backend confirmed account creation.
            this.analytics.trackSignUp();
            this.notificationService.success('User Created', {
              duration: 3000,
              icon: 'check-circle',
            });
          }

          this.store$.dispatch(
            AuthenticationNgRxActions.setAuthSession({
              data: new AuthenticationDataModel({
                id: action.response.user.id,
                token: action.response.authToken,
                refreshToken: action.response.refreshToken,
              }),
            })
          );

          setTimeout(() => {
            this.router.navigateByUrl(
              APP_ROUTES.authentication['confirm-account'].fullPath
            );
          }, 2000);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(createCompanyManagerFailureAction, validateCompanyFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            setTimeout(() => {
              this.isLoading = false;
            }, 1500);
            if (errorMessage == 'The company name already exists.') {
              this.companyExists = errorMessage;
            } else {
              this.notificationService.error(errorMessage, {
                duration: 5000,
                icon: 'alert-outline',
              });
            }
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  onCompanyNameChange(event: any) {
    this.companyExists = '';
    let companyName = event.target.value;
    if (companyName.length > 1) {
      this.validateCompanyName(companyName);
    }
  }

  validateCompanyName(companyName: string) {
    this.store$.dispatch(
      CompanyActions.validateCompany({
        companyName: companyName,
      })
    );
  }

  create() {
    if (this.form.valid && this.isLoading == false)
      this.store$.dispatch(
        UsersActions.createCompanyManager({
          data: this.form.value,
        })
      );
  }

  getParams() {
    const screenWidth = window.screen.width;

    this.activatedRoute.queryParams
      .pipe(map((params) => params))
      .subscribe(async (params: any) => {
        if (params?.fromMobile || screenWidth < 680) {
          localStorage.setItem('from_mobile', 'true');
          if (params.returnUrl) this.url = params.returnUrl;
          localStorage.setItem('return_url', params.returnUrl);
        }
      })
      .unsubscribe();
  }

  onValidateCompanyNameSuccess() {
    this.actions$
      .pipe(
        ofType(validateCompanySuccessAction),
        filter((action: any) => {
          return !isNil(action);
        }),
        map((action: any) => {
          if (action) this.isValid = true;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  emailToLowercase(event: any) {
    this.form.get('email')?.setValue(event.target.value.toLocaleLowerCase());
  }
}
